import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import type { PrepackageDestination } from '../types';
import { DestinationPickerBrowseView } from './destinationPicker/DestinationPickerBrowseView';
import { DestinationPickerDrilldownView } from './destinationPicker/DestinationPickerDrilldownView';
import { DestinationPickerPopover } from './destinationPicker/DestinationPickerPopover';
import { DestinationPickerSearchView } from './destinationPicker/DestinationPickerSearchView';
import { DestinationPickerTrigger } from './destinationPicker/DestinationPickerTrigger';
import { getDestinationName } from './destinationPicker/labels';
import { useDestinationPickerPopover } from './destinationPicker/useDestinationPickerPopover';
import { usePrepackageDestinationHierarchy } from './destinationPicker/usePrepackageDestinationHierarchy';
import {
    buildSearchIndex,
    findPartialMatches,
    type GeoHierarchyNode,
    getDestinationLevel,
    getPopularCountries,
    isSameDestination,
    MAX_PREPACKAGE_DESTINATIONS,
    searchDestinations,
    toggleDestinationSelection,
} from './utils/destinationPicker';
import { trackDestinationPickerOpened, trackDestinationPickerSelect } from './utils/tracking';

type PrepackageDestinationPickerProps = {
    hierarchyUrl?: string;
    selectedDestinations: PrepackageDestination[];
    onChange: (destinations: PrepackageDestination[]) => void;
};

export const PrepackageDestinationPicker = ({ hierarchyUrl, selectedDestinations, onChange }: PrepackageDestinationPickerProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const modalSearchInputRef = useRef<HTMLInputElement>(null);

    const { hierarchy, loading, error, fetchHierarchy } = usePrepackageDestinationHierarchy(hierarchyUrl);
    const isMobilePicker = useBreakpoints({ breakpoint: 950 });
    const [isOpen, setIsOpen] = useState(false);
    const [activeContinent, setActiveContinent] = useState<string>();
    const [drilldownCountry, setDrilldownCountry] = useState<GeoHierarchyNode>();
    const [expandedRegion, setExpandedRegion] = useState<string>();
    const [searchQuery, setSearchQuery] = useState('');

    const closePicker = useCallback(() => {
        setIsOpen(false);
        setSearchQuery('');
        setDrilldownCountry(undefined);
    }, []);

    const closeDrilldown = useCallback(() => {
        setDrilldownCountry(undefined);
    }, []);

    const { popoverStyle, updatePopoverStyle } = useDestinationPickerPopover({
        wrapperRef,
        isOpen,
        isModal: isMobilePicker,
        hasDrilldown: Boolean(drilldownCountry),
        onClose: closePicker,
        onBack: closeDrilldown,
    });

    useEffect(() => {
        if (hierarchy.length > 0 && !activeContinent) {
            setActiveContinent(hierarchy[0].displayName);
        }
    }, [activeContinent, hierarchy]);

    useEffect(() => {
        if (isOpen) {
            trackDestinationPickerOpened();
        }
    }, [isOpen]);

    const searchIndex = useMemo(() => buildSearchIndex(hierarchy), [hierarchy]);
    const activeContinentNode = hierarchy.find((continent) => continent.displayName === activeContinent) ?? hierarchy[0];
    const searchResults = useMemo(() => searchDestinations(searchQuery, searchIndex), [searchIndex, searchQuery]);
    const partialMatches = useMemo(() => findPartialMatches(searchQuery, searchIndex), [searchIndex, searchQuery]);
    const popularCountries = useMemo(() => getPopularCountries(searchIndex, 6), [searchIndex]);
    const isMaxedOut = selectedDestinations.length >= MAX_PREPACKAGE_DESTINATIONS;
    const isSearching = searchQuery.trim().length >= 2;

    const openPicker = (): void => {
        setIsOpen(true);
        updatePopoverStyle();
        void fetchHierarchy();
        window.setTimeout(() => {
            if (isMobilePicker) return;

            inputRef.current?.focus();
        }, 0);
    };

    const isDestinationSelected = useCallback(
        (destination: PrepackageDestination): boolean => selectedDestinations.some((selected) => isSameDestination(selected, destination)),
        [selectedDestinations],
    );

    const toggleDestination = useCallback(
        (destination: PrepackageDestination): void => {
            if (!isDestinationSelected(destination)) {
                trackDestinationPickerSelect(getDestinationName(destination), getDestinationLevel(hierarchy, destination) ?? 'unknown');
            }
            onChange(toggleDestinationSelection({ hierarchy, selected: selectedDestinations, destination }));
        },
        [hierarchy, isDestinationSelected, onChange, selectedDestinations],
    );

    const removeDestination = useCallback(
        (destination: PrepackageDestination): void => {
            onChange(selectedDestinations.filter((selected) => !isSameDestination(selected, destination)));
        },
        [onChange, selectedDestinations],
    );

    const selectCountry = (country: GeoHierarchyNode): void => {
        setDrilldownCountry(country);
        setExpandedRegion(undefined);
    };

    const renderContent = () => {
        if (hierarchy.length === 0) return null;

        if (isSearching) {
            return (
                <DestinationPickerSearchView
                    isMobile={isMobilePicker}
                    searchQuery={searchQuery}
                    searchResults={searchResults}
                    partialMatches={partialMatches}
                    popularCountries={popularCountries}
                    selectedDestinations={selectedDestinations}
                    isDestinationSelected={isDestinationSelected}
                    isMaxedOut={isMaxedOut}
                    onToggleDestination={toggleDestination}
                    onShowAll={() => {
                        setSearchQuery('');
                        setExpandedRegion(undefined);
                    }}
                />
            );
        }

        if (drilldownCountry) {
            return (
                <DestinationPickerDrilldownView
                    isMobile={isMobilePicker}
                    country={drilldownCountry}
                    expandedRegion={expandedRegion}
                    selectedDestinations={selectedDestinations}
                    isDestinationSelected={isDestinationSelected}
                    isMaxedOut={isMaxedOut}
                    onBack={closeDrilldown}
                    onExpandedRegionChange={setExpandedRegion}
                    onToggleDestination={toggleDestination}
                />
            );
        }

        return (
            <DestinationPickerBrowseView
                isMobile={isMobilePicker}
                hierarchy={hierarchy}
                activeContinentNode={activeContinentNode}
                onActiveContinentChange={setActiveContinent}
                onCountrySelect={selectCountry}
            />
        );
    };

    return (
        <div className="relative min-w-0" ref={wrapperRef}>
            <DestinationPickerTrigger
                inputRef={inputRef}
                isMobile={isMobilePicker}
                isOpen={isOpen}
                searchQuery={searchQuery}
                selectedDestinations={selectedDestinations}
                onOpen={openPicker}
                onSearchQueryChange={setSearchQuery}
                onReset={() => onChange([])}
            />
            {isOpen && (
                <DestinationPickerPopover
                    variant={isMobilePicker ? 'modal' : 'popover'}
                    style={isMobilePicker ? undefined : popoverStyle}
                    searchInputRef={modalSearchInputRef}
                    searchQuery={searchQuery}
                    onSearchQueryChange={setSearchQuery}
                    selectedDestinations={selectedDestinations}
                    loading={loading}
                    error={error}
                    isMaxedOut={isMaxedOut}
                    onRemoveDestination={removeDestination}
                    onReset={() => onChange([])}
                    onApply={closePicker}>
                    {renderContent()}
                </DestinationPickerPopover>
            )}
        </div>
    );
};
